import React, { useEffect, useMemo, useRef, useState } from "react";
import useShellScrollLock from "../../hooks/useShellScrollLock";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import NotFound from "../../Views/NotFound/NotFound";
import { AdminPageSkeleton } from "../Loading/AdminDataSkeleton";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import { useMediaQuery } from "@mui/material";
// JOB-6314: the donor shell was the ONLY authenticated layout with no MUI theme.
import { ThemeProvider, createTheme } from "@mui/material/styles";
// JOB-7066: merchant branding (palette for the viewer's appearance, fonts).
import { BrandContext, useCompanyBrand } from "../../contexts/BrandContext";
import { withBrandTheme } from "../../utils/paintBrand";
import { Grid } from "@mui/material";
import {
  DonorDarkModeProvider,
  useDonorDarkMode,
} from "../../contexts/DonorDarkModeContext";
// JOB-5873: same title-setting utility the sibling layouts already use.
import { setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";
import { applyColorScheme } from "../../utils/colorScheme";

// JOB-3700: split the layout body so we can read DonorDarkModeContext
// inside it. The outer <Donor /> renders the provider; this inner
// component consumes it.
const DonorBody = () => {
  const mainContent = useRef(null);
  const scrollShell = useRef(null);
  // JOB-7517: the shell is the only scroller; the document must not scroll (see useShellScrollLock).
  useShellScrollLock();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  const isMediumScreen = useMediaQuery("(max-width:767px)", { noSsr: true });
  const { darkMode } = useDonorDarkMode();
  const brand = useCompanyBrand(darkMode);

  /* JOB-6314 — the donor portal had NO ThemeProvider at all, unlike Admin.jsx,
     Client.jsx, StaffMember.jsx and SuperAdmin.jsx, which each install one. The
     consequence was that theme.palette.mode was permanently "light" here, so:

       - every unstyled MUI component rendered its LIGHT value on a dark page
         (Paper -> #fff, color="text.secondary" -> rgba(0,0,0,.6) at 1.35:1,
         TextField borders, Chip, Alert, Divider, Tooltip);
       - nothing that PORTALS could be reached, because MUI carries the theme
         into portals through React context and there was no theme to carry --
         which is why body.donor-dark-active only ever grew 14 rules against the
         admin side's 1100;
       - and any donor-portal code branching on palette.mode === "dark" was dead
         code. DonorProfile.jsx:119 is the clearest case: isDark is used 12
         times there and has never once been true.

     The dark values are taken from donor-dark.css rather than invented, so the
     theme agrees with the 548 rules already written against this shell:
       #0f2b46  page background, and already this Grid's own backgroundColor
       #18324f  dominant donor card surface (106 uses)
       #e8eaed  dominant donor text colour (155 uses)
       #cbd5e1  donor secondary text (21 uses)  -> 8.80:1 on #18324f
       #29B3E2  donor accent (60 uses)          -> 5.37:1 on #18324f

     LIGHT mode deliberately sets ONLY `mode`. There is no provider today, so
     light currently renders with MUI's stock defaults; specifying anything else
     on the light branch would CHANGE light mode, which this ticket must not do.
     That is also why this does not copy Admin.jsx's light primary/secondary/
     background values -- those are the admin shell's choices, not the donor's. */
  const donorTheme = useMemo(
    () =>
      createTheme(
        withBrandTheme({
          palette: darkMode
            ? {
                mode: "dark",
                primary: { main: "#29B3E2" }, // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
                background: { default: "#0f2b46", paper: "#18324f" }, // brand-exempt: MUI palette value (MUI color math cannot read a CSS variable)
                text: { primary: "#e8eaed", secondary: "#cbd5e1" },
                divider: "rgba(255, 255, 255, 0.12)",
              }
            : { mode: "light" },
          components: {
            // Suppresses MUI's dark-mode elevation gradient so Paper matches the
            // flat donor surfaces. A no-op in light mode, where it is already none.
            MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
          },
        }, brand.data, darkMode)
      ),
    [darkMode, brand.data]
  );

  // JOB-5687: sendSwal mounts CustomSwal into a div appended straight to
  // document.body, so it lives OUTSIDE .donor-portal-layout and every donor
  // dark rule scoped to that class misses it -- which is why the confirm
  // dialogs stayed white while the page behind them was navy. The admin
  // shells publish `admin-dark-active` on <body> for exactly this reason;
  // the donor portal gets its own flag rather than reusing that one, because
  // those rules carry the admin slate palette (#1a2332) rather than the donor
  // navy, and 122 further rules in donor-dark.css key off admin-dark-active.
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("donor-dark-active");
    } else {
      document.body.classList.remove("donor-dark-active");
    }
    // LIMO-205: <html> has to say the same thing the page looks like. The
    // browser reads color-scheme for native controls and scrollbars, and CSS
    // reads the attribute; index.html sets both to light before React boots
    // and nothing used to update them afterwards.
    applyColorScheme(darkMode);
    return () => {
      document.body.classList.remove("donor-dark-active");
      applyColorScheme(false);
    };
  }, [darkMode]);

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMediumScreen) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMediumScreen]);

  useEffect(() => {
    // JOB-5088: the real scroller is the outer 100vh/overflow:auto
    // `.donor-portal-layout` Grid, not `mainContent` (no scroll context).
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
    if (scrollShell.current) {
      scrollShell.current.scrollTop = 0;
    }

    // JOB-5873: every sibling layout (Admin/Client/StaffMember/SuperAdmin) sets
    // the tab title from the route on navigation -- this one never did, so
    // every donor-portal-layout route kept whatever title the PREVIOUS layout
    // had left behind (or the bare default on first load). Same call, same
    // [location] dependency, as Client.jsx's equivalent effect.
    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === "/:CompanyUrl/donors" && !prop.isCollapse) {
        return (
          <Route path={prop?.path} element={prop?.component} key={key} exact />
        );
      } else {
        if (prop.children) {
          return prop?.children?.map((item) => {
            return (
              <Route
                path={item?.path}
                element={item?.component}
                key={key}
                exact
              />
            );
          });
        }
      }
    });
  };

  return (
    <BrandContext.Provider value={brand}>
    <ThemeProvider theme={donorTheme}>
    <Grid
      ref={scrollShell}
      // JOB-3700: .donor-portal-layout only attaches in dark mode so the
      // 1900+ lines of donor-dark.css overrides (all scoped to that
      // class) lie dormant when the donor picks Light / system-light.
      // backgroundColor flips too so the wrapper itself isn't a dark
      // slab under light-mode children.
      className={darkMode ? "donor-portal-layout" : "donor-portal-layout--light"}
      style={{
        backgroundColor: darkMode ? "var(--cj-b-deep, #0f2b46)" : "#f8f9fa",
        overflow: "auto",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl/donors`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />

      <MainNav
        layout="/:CompanyUrl/donors"
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        isSidebarClosed={isSidebarClosed}
      />
      <Grid
        className="main-content"
        style={{
          marginLeft: !isMediumScreen
            ? isSidebarClosed
              ? "150px"
              : "300px"
            : "15px",
        }}
        ref={mainContent}
      >
        <Grid style={{ width: "95%" }}>
          <React.Suspense fallback={<AdminPageSkeleton minHeight="60vh" />}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-3220 + JOB-3225: real 404 instead of silent /index redirect */}
              <Route path="*" element={<NotFound homePath={`/${CompanyUrl}/donors/index`} />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
    </BrandContext.Provider>
  );
};

const Donor = () => (
  <DonorDarkModeProvider>
    <DonorBody />
  </DonorDarkModeProvider>
);

export default Donor;
